import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, PenTool, ArrowLeft, Loader2, Image, CheckCircle2,
  Filter, Download, Upload, X, Search,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import { toast } from '@/lib/toast'
import { annotationsApi } from '@/api/endpoints'
import { AnnotationEditor } from '@/components/annotations/AnnotationEditor'
import type { AnnotationProject, AnnotationClassDef, ImportFilters } from '@/types'

// ============================================================================
// Project List View
// ============================================================================

function ProjectList({ onSelect }: { onSelect: (project: AnnotationProject) => void }) {
  const { t } = useTranslation('common')
  const d = useDirectionStore((s) => s.direction)
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['annotation-projects'],
    queryFn: () => annotationsApi.listProjects(),
    staleTime: 30_000,
  })

  const projects = data?.results ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={cn('text-xl sm:text-2xl font-semibold tracking-tight', d === 'expressive' && 'font-display')}>{t('nav.annotations')}</h2>
          <p className="text-sm text-muted-foreground">Manage annotation workspaces and import data for model fine-tuning</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Workspace
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}><CardContent className="p-4 space-y-3">
              <div className="shimmer h-4 w-32 rounded" />
              <div className="shimmer h-3 w-24 rounded" />
            </CardContent></Card>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="flex h-48 flex-col items-center justify-center text-center gap-3">
            <PenTool className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No annotation workspaces yet</p>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create your first workspace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const pct = project.task_count > 0 ? Math.round((project.completed_count / project.task_count) * 100) : 0
            return (
              <Card
                key={project.id}
                className="cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all"
                onClick={() => onSelect(project)}
              >
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center gap-2">
                    <PenTool className="h-4 w-4 text-primary" />
                    <CardTitle className="text-sm font-semibold truncate">{project.name}</CardTitle>
                  </div>
                  {project.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{project.description}</p>
                  )}
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Images</p>
                      <p className="text-xs font-semibold">{project.task_count}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Annotations</p>
                      <p className="text-xs font-semibold">{project.annotation_count}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Completed</p>
                      <p className="text-xs font-semibold">{pct}%</p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {/* Class chips */}
                  <div className="flex flex-wrap gap-1">
                    {project.class_set.slice(0, 5).map((cls) => (
                      <span
                        key={cls.id}
                        className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-medium"
                        style={{ backgroundColor: cls.color + '20', color: cls.color }}
                      >
                        {cls.name}
                      </span>
                    ))}
                    {project.class_set.length > 5 && (
                      <span className="text-[9px] text-muted-foreground">+{project.class_set.length - 5}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(p) => {
        queryClient.invalidateQueries({ queryKey: ['annotation-projects'] })
        setCreateOpen(false)
        onSelect(p)
      }} />
    </div>
  )
}

// ============================================================================
// Create Project Dialog
// ============================================================================

function CreateProjectDialog({
  open, onOpenChange, onCreated
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (project: AnnotationProject) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [usePreset, setUsePreset] = useState(true)

  const { data: classesData } = useQuery({
    queryKey: ['available-classes'],
    queryFn: () => annotationsApi.availableClasses(),
    enabled: open,
  })

  const COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#6366f1','#a855f7','#ec4899','#f43f5e']

  const createMutation = useMutation({
    mutationFn: () => {
      const class_set: AnnotationClassDef[] = usePreset
        ? (classesData?.classes ?? []).map((name, i) => ({ id: i, name, color: COLORS[i % COLORS.length] }))
        : []
      return annotationsApi.createProject({ name: name.trim(), description: description.trim(), class_set })
    },
    onSuccess: (project) => {
      toast.success(`Workspace "${project.name}" created`)
      setName('')
      setDescription('')
      onCreated(project)
    },
    onError: () => toast.error('Failed to create workspace'),
  })

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">Create Workspace</Dialog.Title>
          <div className="px-5 py-4 border-b border-border/60">
            <p className="text-sm font-semibold">Create Annotation Workspace</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name *</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. DeterCam Domain Data" className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Initial Class Set</label>
              <div className="flex gap-2">
                <Button size="sm" variant={usePreset ? 'default' : 'outline'} className="text-xs h-8" onClick={() => setUsePreset(true)}>
                  COCO-80 Preset ({classesData?.classes.length ?? '...'} classes)
                </Button>
                <Button size="sm" variant={!usePreset ? 'default' : 'outline'} className="text-xs h-8" onClick={() => setUsePreset(false)}>
                  Start Empty
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">You can add custom classes later at any time</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" className="h-9 text-xs">Cancel</Button>
            </Dialog.Close>
            <Button size="sm" className="h-9 text-xs gap-1.5" disabled={!name.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ============================================================================
// Project Detail View — task list + import + stats
// ============================================================================

function ProjectDetail({ project, onBack }: { project: AnnotationProject; onBack: () => void }) {
  const d = useDirectionStore((s) => s.direction)
  const queryClient = useQueryClient()
  const [importOpen, setImportOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [editorTaskId, setEditorTaskId] = useState<number | null>(null)

  const { data: projectData } = useQuery({
    queryKey: ['annotation-project', project.id],
    queryFn: () => annotationsApi.getProject(project.id),
    staleTime: 30_000,
  })
  const p = projectData ?? project

  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['annotation-tasks', project.id, statusFilter, page],
    queryFn: () => annotationsApi.listTasks(project.id, {
      status: statusFilter || undefined,
      page,
    }),
    staleTime: 15_000,
  })

  const tasks = tasksData?.results ?? []
  const totalTasks = tasksData?.count ?? 0

  const handleOpenTask = (taskId: number) => {
    setEditorTaskId(taskId)
  }

  // Show editor if a task is selected
  if (editorTaskId) {
    return (
      <AnnotationEditor
        projectId={project.id}
        taskId={editorTaskId}
        classSet={p.class_set}
        onBack={() => {
          setEditorTaskId(null)
          queryClient.invalidateQueries({ queryKey: ['annotation-tasks', project.id] })
        }}
        onNavigate={(newTaskId) => setEditorTaskId(newTaskId)}
      />
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h3 className={cn('text-lg font-semibold truncate', d === 'expressive' && 'font-display')}>{p.name}</h3>
          {p.description && <p className="text-xs text-muted-foreground truncate">{p.description}</p>}
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setImportOpen(true)}>
          <Upload className="h-3.5 w-3.5" />
          Import
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setExportOpen(true)} disabled={p.completed_count === 0}>
          <Download className="h-3.5 w-3.5" />
          Export YOLO
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Images', value: p.task_count, icon: <Image className="h-4 w-4" /> },
          { label: 'Annotations', value: p.annotation_count, icon: <PenTool className="h-4 w-4" /> },
          { label: 'Completed', value: p.completed_count, icon: <CheckCircle2 className="h-4 w-4" /> },
          { label: 'Classes', value: p.class_set.length, icon: <Filter className="h-4 w-4" /> },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-3 flex items-center gap-2">
              <div className="text-muted-foreground">{stat.icon}</div>
              <div>
                <p className="text-xs font-semibold">{stat.value}</p>
                <p className="text-[10px] text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Class chips */}
      {p.class_set.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {p.class_set.map((cls) => (
            <span
              key={cls.id}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ backgroundColor: cls.color + '20', color: cls.color }}
            >
              {cls.name}
            </span>
          ))}
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2">
        {['', 'pending', 'completed', 'reviewed'].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            className="text-xs h-7"
            onClick={() => { setStatusFilter(s); setPage(1) }}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground self-center">{totalTasks} images</span>
      </div>

      {/* Task grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="aspect-[4/3] rounded-lg shimmer" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <Card>
          <CardContent className="flex h-32 flex-col items-center justify-center text-center gap-2">
            <Image className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">
              {statusFilter ? 'No images with this status' : 'No images yet — import from the detection pipeline'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="relative group cursor-pointer rounded-lg overflow-hidden bg-muted aspect-[4/3] hover:ring-1 hover:ring-primary/40 transition-all"
                onClick={() => handleOpenTask(task.id)}
              >
                {/* Status badge — §5 color mapping applied to emerald/amber/blue literals */}
                <div className="absolute top-1 left-1 z-10">
                  {d === 'current' ? (
                    <span className={cn(
                      'text-[9px] font-medium px-1.5 py-0.5 rounded-full',
                      task.status === 'completed' ? 'bg-emerald-500/80 text-white' :
                      task.status === 'reviewed' ? 'bg-blue-500/80 text-white' :
                      task.status === 'in_progress' ? 'bg-amber-500/80 text-white' :
                      'bg-white/80 text-black'
                    )}>
                      {task.status}
                    </span>
                  ) : d === 'command' ? (
                    <span className={cn(
                      'text-[9px] font-mono uppercase px-1.5 py-0.5 border',
                      task.status === 'completed' ? 'border-[hsl(150,65%,45%)] text-[hsl(150,65%,45%)]' :
                      task.status === 'reviewed' ? 'border-primary/50 text-primary' :
                      task.status === 'in_progress' ? 'border-primary/50 text-primary' :
                      'border-border text-muted-foreground'
                    )}>
                      {task.status}
                    </span>
                  ) : d === 'saas' ? (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {task.status}
                    </span>
                  ) : (
                    /* expressive: keep hue, add rounded-full */
                    <span className={cn(
                      'text-[9px] font-medium px-1.5 py-0.5 rounded-full',
                      task.status === 'completed' ? 'bg-emerald-500/80 text-white' :
                      task.status === 'reviewed' ? 'bg-blue-500/80 text-white' :
                      task.status === 'in_progress' ? 'bg-amber-500/80 text-white' :
                      'bg-white/80 text-black'
                    )}>
                      {task.status}
                    </span>
                  )}
                </div>
                {/* Annotation count */}
                <div className="absolute top-1 right-1 z-10">
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-black/60 text-white">
                    {task.annotation_count ?? 0} bbox
                  </span>
                </div>
                {/* Filename */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                  <p className="text-[9px] text-white truncate">{task.s3_key.split('/').pop()}</p>
                </div>
                {/* Thumbnail */}
                {task.image_url ? (
                  <img src={task.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="h-6 w-6 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalTasks > 20 && (
            <div className="flex justify-center gap-2 pt-2">
              <Button size="sm" variant="outline" className="text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="self-center text-xs text-muted-foreground">
                Page {page} of {Math.ceil(totalTasks / 20)}
              </span>
              <Button size="sm" variant="outline" className="text-xs" disabled={page >= Math.ceil(totalTasks / 20)} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={project.id}
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: ['annotation-tasks', project.id] })
          queryClient.invalidateQueries({ queryKey: ['annotation-project', project.id] })
          queryClient.invalidateQueries({ queryKey: ['annotation-projects'] })
        }}
      />

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        projectId={project.id}
        classSet={p.class_set}
      />
    </div>
  )
}

// ============================================================================
// Import Dialog
// ============================================================================

function ImportDialog({
  open, onOpenChange, projectId, onImported
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  onImported: () => void
}) {
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])
  const [classSearch, setClassSearch] = useState('')
  const [minConfidence, setMinConfidence] = useState(0.3)

  const { data: classesData } = useQuery({
    queryKey: ['available-classes'],
    queryFn: () => annotationsApi.availableClasses(),
    enabled: open,
  })
  const allClasses = classesData?.classes ?? []
  const filteredClasses = classSearch
    ? allClasses.filter(c => c.toLowerCase().includes(classSearch.toLowerCase()))
    : allClasses

  const filters: ImportFilters = {
    classes: selectedClasses,
    min_confidence: minConfidence,
    include_all_boxes: true,
  }

  const previewQuery = useQuery({
    queryKey: ['import-preview', projectId, selectedClasses, minConfidence],
    queryFn: () => annotationsApi.importPreview(projectId, filters),
    enabled: open && selectedClasses.length > 0,
    staleTime: 10_000,
  })

  const importMutation = useMutation({
    mutationFn: () => annotationsApi.importFromPipeline(projectId, filters),
    onSuccess: (result) => {
      toast.success(`Imported ${result.imported_tasks} images with ${result.imported_annotations} annotations`)
      onOpenChange(false)
      onImported()
    },
    onError: () => toast.error('Import failed'),
  })

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">Import Images</Dialog.Title>

          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
            <p className="text-sm font-semibold">Import from Detection Pipeline</p>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8"><X className="h-4 w-4" /></Button>
            </Dialog.Close>
          </div>

          <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Class filter */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Filter by detection class (select at least one)
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={classSearch}
                  onChange={e => setClassSearch(e.target.value)}
                  placeholder="Search classes..."
                  className="h-8 text-xs pl-8"
                />
              </div>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 grid grid-cols-3 gap-1">
                {filteredClasses.map(cls => (
                  <label key={cls} className={cn(
                    'flex items-center gap-1.5 rounded px-2 py-1 text-xs cursor-pointer transition-colors',
                    selectedClasses.includes(cls) ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
                  )}>
                    <input
                      type="checkbox"
                      className="h-3 w-3 accent-primary"
                      checked={selectedClasses.includes(cls)}
                      onChange={() => setSelectedClasses(prev =>
                        prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
                      )}
                    />
                    {cls}
                  </label>
                ))}
              </div>
              {selectedClasses.length > 0 && (
                <p className="text-[10px] text-primary mt-1">{selectedClasses.length} class(es) selected</p>
              )}
            </div>

            {/* Confidence */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Min confidence: {minConfidence.toFixed(1)}
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={minConfidence}
                onChange={e => setMinConfidence(parseFloat(e.target.value))}
                className="w-full h-1.5 accent-primary"
              />
            </div>

            {/* Preview */}
            {selectedClasses.length > 0 && (
              <div className="rounded-lg border bg-muted/30 p-3">
                {previewQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Counting matching images...
                  </div>
                ) : previewQuery.data ? (
                  <div className="text-xs space-y-0.5">
                    <p><strong>{previewQuery.data.new_images}</strong> new images to import</p>
                    <p className="text-muted-foreground">{previewQuery.data.already_imported} already imported (skipped)</p>
                    <p className="text-muted-foreground">{previewQuery.data.total_matching} total matching</p>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" className="h-9 text-xs">Cancel</Button>
            </Dialog.Close>
            <Button
              size="sm"
              className="h-9 text-xs gap-1.5"
              disabled={selectedClasses.length === 0 || importMutation.isPending || (previewQuery.data?.new_images === 0)}
              onClick={() => importMutation.mutate()}
            >
              {importMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Import {previewQuery.data?.new_images ?? ''} Images
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ============================================================================
// Export Dialog
// ============================================================================

function ExportDialog({
  open, onOpenChange, projectId, classSet,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  classSet: AnnotationClassDef[]
}) {
  const [trainSplit, setTrainSplit] = useState(0.8)
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])
  const [includeAll, setIncludeAll] = useState(true)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)

  const allClassNames = classSet.map(c => c.name)

  const exportMutation = useMutation({
    mutationFn: () => annotationsApi.exportYolo(projectId, {
      classes: selectedClasses.length > 0 ? selectedClasses : undefined,
      status: ['completed', 'reviewed'],
      train_split: trainSplit,
      include_all_labels: includeAll,
    }),
    onSuccess: (data) => {
      setDownloadUrl(data.download_url)
      toast.success('Export complete — ready to download')
    },
    onError: () => toast.error('Export failed — no matching annotations'),
  })

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setDownloadUrl(null) }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">Export YOLO Dataset</Dialog.Title>

          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
            <p className="text-sm font-semibold">Export YOLO Dataset</p>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8"><X className="h-4 w-4" /></Button>
            </Dialog.Close>
          </div>

          <div className="px-5 py-4 space-y-4">
            {/* Class filter (optional) */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Classes to export (empty = all)
              </label>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 grid grid-cols-3 gap-1">
                {allClassNames.map(cls => (
                  <label key={cls} className={cn(
                    'flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] cursor-pointer transition-colors',
                    selectedClasses.includes(cls) ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
                  )}>
                    <input
                      type="checkbox"
                      className="h-3 w-3 accent-primary"
                      checked={selectedClasses.includes(cls)}
                      onChange={() => setSelectedClasses(prev =>
                        prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
                      )}
                    />
                    {cls}
                  </label>
                ))}
              </div>
            </div>

            {/* Include all labels */}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={includeAll} onChange={e => setIncludeAll(e.target.checked)} />
              Include all labels on matching images (recommended for training)
            </label>

            {/* Train/Val split */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Train / Val split: {Math.round(trainSplit * 100)}% / {Math.round((1 - trainSplit) * 100)}%
              </label>
              <input
                type="range" min={0.5} max={0.95} step={0.05} value={trainSplit}
                onChange={e => setTrainSplit(parseFloat(e.target.value))}
                className="w-full h-1.5 accent-primary"
              />
            </div>

            {/* Download link */}
            {downloadUrl && (
              <div className="rounded-lg border bg-emerald-500/5 border-emerald-500/20 p-3 text-center">
                <p className="text-xs text-emerald-600 font-medium mb-2">Export ready!</p>
                <a
                  href={downloadUrl}
                  download="dataset.zip"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-600 transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download dataset.zip
                </a>
                <p className="text-[10px] text-muted-foreground mt-2">Link expires in 24 hours</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" className="h-9 text-xs">Close</Button>
            </Dialog.Close>
            {!downloadUrl && (
              <Button
                size="sm"
                className="h-9 text-xs gap-1.5"
                disabled={exportMutation.isPending}
                onClick={() => exportMutation.mutate()}
              >
                {exportMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {exportMutation.isPending ? 'Exporting...' : 'Generate Export'}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ============================================================================
// Main Page — switches between list and detail
// ============================================================================

export function AnnotationsPage() {
  const [selectedProject, setSelectedProject] = useState<AnnotationProject | null>(null)

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-up">
      {!selectedProject ? (
        <ProjectList onSelect={setSelectedProject} />
      ) : (
        <ProjectDetail project={selectedProject} onBack={() => setSelectedProject(null)} />
      )}
    </div>
  )
}
